// =============================================================================
// readErrorText — the operator sentence for a read that FAILED.
//
// The write path has gone through friendlyError repo-wide since the §5.6 sweep.
// The read path never did: <QueryStateGate/>'s red card interpolated
// `error.message` verbatim, so 13 of 14 farm registers rendered
// "Couldn't load chemicals / relation \"chemical_balances\" does not exist"
// (docs/audits/FARM-UI-AUDIT-2026-09-02 §1). check-operator-vocabulary scans
// source STRINGS, and a runtime driver message is invisible to it — which is
// why this gap outlived the sweep that closed the write side.
//
// friendlyError stays the one mapping; this only decides what to do with text
// it does NOT map. Its fallback ("the raw text is the best we have; don't hide
// it") is right for a WRITE — the operator just acted, and the driver text is
// the thing they can quote back. A read fails on its own, unbidden, so the same
// string is noise inside a red border, and it teaches an operator that the app
// talks nonsense at them.
//
// Nothing is lost by dropping it: main.tsx subscribes to the QueryCache and
// reports EVERY failed read in full (reportError → client_errors/Sentry) plus a
// per-screen query_error friction stamp. Support already has the raw text; the
// screen is the one place it does not belong. Hence a string, not a
// {message, detail} pair — the read card has no Details disclosure to fill, and
// the invariant it can then hold is absolute: no driver text on screen, ever.
// =============================================================================

import { ValidationError } from '@/lib/errorReporter'
import { friendlyError, type Friendly } from '@/lib/friendlyError'

/** Shown when the read failed for a reason we have no operator sentence for. */
export const READ_FAILED_MESSAGE =
  'Something went wrong on the server. Try again — if it keeps failing, report it to your administrator.'

// Every branch friendlyError MAPS sets `detail` to the raw text; only its three
// passthroughs leave it null — a ValidationError, our own P0001 RAISE, and the
// unknown fallback. So "detail is null, and not one of the two prose
// passthroughs" IS the unmapped set, read off friendlyError's own contract
// rather than guessed at from the wording.
function isUnmapped(err: unknown, f: Friendly): boolean {
  if (err instanceof ValidationError) return false
  if (f.code === 'P0001') return false
  return f.detail === null
}

// An unmapped message is replaced only when it looks like it came from the
// driver rather than from us — plenty of queryFns throw prose of their own
// ("Access not restored — you may not have permission…"), and that prose beats
// any generic sentence. A surviving PG/PostgREST code is proof on its own; the
// patterns cover the wrappers that destroy `.code`, which friendlyError's
// header records as the norm here, not the edge.
const DRIVER_TEXT: RegExp[] = [
  /^\[[A-Za-z0-9]{5,8}\]/,                                            // "[42P01] …" — a code the message kept
  /^PGRST\d+/,
  /\b(relation|column|table|function|type|schema|role|operator)\b.*\bdoes not exist\b/i,
  /\bschema cache\b/i,
  /\bviolates\b.*\bconstraint\b/i,
  /\bsyntax error\b/i,
  /\brow-level security\b/i,
  /\bpermission denied for\b/i,
  /\bJSON object requested\b/i,
]

/** A failed read → one operator sentence. Never driver text. */
export function readErrorText(err: unknown): string {
  const f = friendlyError(err)
  if (!isUnmapped(err, f)) return f.message
  const driverText = f.code != null || DRIVER_TEXT.some(re => re.test(f.message))
  return driverText ? READ_FAILED_MESSAGE : f.message
}
